import React, { useRef, useCallback, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Send, Loader2, Paperclip, X, FileText, Zap, ImageIcon, Reply, StickyNote, MessageSquare } from 'lucide-react';
import { EmojiPicker } from '@/components/whatsapp/EmojiPicker';
import { AudioRecorder } from '@/components/whatsapp/AudioRecorder';
import { cn } from '@/lib/utils';
import type { Message } from '../hooks/useConversation';

interface MessageInputProps {
  value: string;
  onChange: (val: string) => void;
  onSend: () => void;
  onSendAudio: (blob: Blob) => Promise<void>;
  onFileSelect: (file: File) => Promise<void>;
  onOpenTemplates: () => void;
  onSendNote?: (text: string) => Promise<void> | void;
  selectedFile: File | null;
  isUploading: boolean;
  onClearFile: () => void;
  isSending: boolean;
  disabled?: boolean;
  replyingTo?: Message | null;
  onCancelReply?: () => void;
}

function previewBody(msg: Message): string {
  if (msg.body) {
    // strip leading quote if present
    const stripped = msg.body.replace(/^>\s?.+?(?:\n\n|\n)/, '');
    return stripped.slice(0, 120);
  }
  if (msg.media_mimetype?.startsWith('image/')) return '📷 Imagem';
  if (msg.media_mimetype?.startsWith('audio/')) return '🎤 Mensagem de voz';
  if (msg.media_mimetype?.startsWith('video/')) return '🎬 Vídeo';
  return 'Anexo';
}

export function MessageInput({
  value,
  onChange,
  onSend,
  onSendAudio,
  onFileSelect,
  onOpenTemplates,
  onSendNote,
  selectedFile,
  isUploading,
  onClearFile,
  isSending,
  disabled,
  replyingTo,
  onCancelReply,
}: MessageInputProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [mode, setMode] = useState<'reply' | 'note'>('reply');
  const isNote = mode === 'note' && !!onSendNote;

  const handleSendClick = useCallback(() => {
    if (isNote) {
      const text = value.trim();
      if (!text || !onSendNote) return;
      const result = onSendNote(text);
      Promise.resolve(result).then(() => {
        onChange('');
        if (textareaRef.current) textareaRef.current.style.height = 'auto';
      });
      return;
    }
    onSend();
  }, [isNote, value, onSendNote, onSend, onChange]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendClick();
    }
  }, [handleSendClick]);

  const handleTextareaChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    onChange(e.target.value);
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 120) + 'px';
    }
  }, [onChange]);

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) onFileSelect(file);
    e.target.value = '';
  }, [onFileSelect]);

  const switchMode = useCallback((next: 'reply' | 'note') => {
    if (next === mode) return;
    setMode(next);
    if (next === 'note' && onCancelReply) onCancelReply();
  }, [mode, onCancelReply]);

  const hasContent = isNote ? !!value.trim() : (value.trim() || selectedFile);
  const replyIsOutbound = replyingTo?.direction === 'outbound';

  return (
    <div className={cn(
      'flex-shrink-0',
      isNote ? 'bg-amber-50 dark:bg-amber-950/40 border-t-2 border-amber-400' : 'bg-wa-header'
    )}>
      {/* Mode toggle */}
      {onSendNote && (
        <div className="flex items-center gap-1 px-3 pt-2">
          <button
            type="button"
            onClick={() => switchMode('reply')}
            className={cn(
              'flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium transition-colors',
              !isNote
                ? 'bg-wa-green text-white shadow-sm'
                : 'bg-transparent text-wa-text-muted hover:bg-wa-sidebar-active'
            )}
          >
            <MessageSquare className="w-3.5 h-3.5" />
            Responder
          </button>
          <button
            type="button"
            onClick={() => switchMode('note')}
            className={cn(
              'flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium transition-colors',
              isNote
                ? 'bg-amber-400 text-amber-950 shadow-sm'
                : 'bg-transparent text-wa-text-muted hover:bg-wa-sidebar-active'
            )}
          >
            <StickyNote className="w-3.5 h-3.5" />
            Nota interna
          </button>
        </div>
      )}

      {/* Reply preview (hidden in note mode) */}
      {!isNote && replyingTo && (
        <div className="px-3 pt-2">
          <div className={cn(
            'flex items-center gap-2 bg-wa-in dark:bg-wa-in rounded-lg p-2 border-l-[3px] shadow-sm',
            replyIsOutbound ? 'border-wa-reply-out' : 'border-wa-reply-in'
          )}>
            <Reply className="w-4 h-4 text-wa-text-muted flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className={cn(
                'text-[12px] font-semibold',
                replyIsOutbound ? 'text-wa-reply-out' : 'text-wa-reply-in'
              )}>
                {replyIsOutbound ? 'Você' : 'Contato'}
              </p>
              <p className="text-[13px] text-wa-text-muted truncate">{previewBody(replyingTo)}</p>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={onCancelReply}
              className="h-7 w-7 flex-shrink-0 hover:bg-wa-sidebar-active"
            >
              <X className="w-4 h-4 text-wa-text-muted" />
            </Button>
          </div>
        </div>
      )}

      <div className="p-3">
        {/* File preview */}
        {selectedFile && (
          <div className="mb-2 p-2 bg-wa-in rounded-lg flex items-center gap-2 shadow-sm">
            <div className="flex items-center gap-2 flex-1 min-w-0">
              {selectedFile.type.startsWith('image/') ? (
                <ImageIcon className="w-5 h-5 text-wa-green flex-shrink-0" />
              ) : (
                <FileText className="w-5 h-5 text-wa-green flex-shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate text-wa-text">{selectedFile.name}</p>
                <p className="text-xs text-wa-text-muted">
                  {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                </p>
              </div>
            </div>
            {isUploading ? (
              <Loader2 className="w-5 h-5 animate-spin text-wa-text-muted" />
            ) : (
              <Button variant="ghost" size="icon" onClick={onClearFile} className="h-8 w-8 flex-shrink-0">
                <X className="w-4 h-4" />
              </Button>
            )}
          </div>
        )}

        <div className="flex items-end gap-2">
          {/* Emoji / Attach / Templates / Audio — hidden in note mode */}
          {!isNote && (
            <>
              <EmojiPicker onEmojiSelect={(emoji) => onChange(value + emoji)} disabled={disabled} />

              <Button
                variant="ghost"
                size="icon"
                className="h-10 w-10 rounded-full hover:bg-wa-sidebar-active text-wa-text-muted flex-shrink-0"
                disabled={isUploading || disabled}
                onClick={() => fileInputRef.current?.click()}
              >
                {isUploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Paperclip className="w-5 h-5" />}
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx"
                onChange={handleFileChange}
              />

              <Button
                variant="ghost"
                size="icon"
                onClick={onOpenTemplates}
                disabled={disabled}
                className="h-10 w-10 rounded-full hover:bg-wa-sidebar-active text-wa-text-muted flex-shrink-0"
              >
                <Zap className="w-5 h-5" />
              </Button>
            </>
          )}

          <div className="flex-1 min-w-0">
            <Textarea
              ref={textareaRef}
              placeholder={isNote ? 'Escrever nota interna (só a equipe vê)...' : 'Digite uma mensagem'}
              value={value}
              onChange={handleTextareaChange}
              onKeyDown={handleKeyDown}
              disabled={disabled || isSending}
              className={cn(
                'min-h-[42px] max-h-[120px] resize-none rounded-lg py-2.5 shadow-sm focus-visible:ring-0 focus-visible:ring-offset-0',
                isNote
                  ? 'bg-amber-100/70 dark:bg-amber-900/40 text-amber-950 dark:text-amber-50 placeholder:text-amber-700/60 dark:placeholder:text-amber-200/60 border border-amber-300 dark:border-amber-700'
                  : 'border-0 bg-wa-in text-wa-text placeholder:text-wa-text-muted'
              )}
              rows={1}
            />
          </div>

          {/* Audio (reply mode, empty) or Send */}
          {!isNote && !hasContent ? (
            <AudioRecorder onAudioReady={onSendAudio} disabled={disabled || isSending} />
          ) : (
            <button
              onClick={handleSendClick}
              disabled={!hasContent || isSending || disabled}
              className={cn(
                'p-2.5 rounded-full flex-shrink-0 transition-all',
                !hasContent || isSending
                  ? 'bg-wa-text-muted/30 cursor-not-allowed'
                  : isNote
                    ? 'bg-amber-400 hover:bg-amber-500 shadow-md'
                    : 'bg-wa-green hover:bg-wa-green/90 shadow-md'
              )}
            >
              {isSending ? (
                <Loader2 className={cn('w-5 h-5 animate-spin', isNote ? 'text-amber-950' : 'text-white')} />
              ) : (
                <Send className={cn('w-5 h-5', isNote ? 'text-amber-950' : 'text-white')} />
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
